import apiService from "@/lib/api";

const BASE = "/api/v2/outreach/sequences";

export const SequenceService = {
  list: async ({ page = 1, pageSize = 20, status, q, campaignId, ownerId, deletedOnly = false, includeDeleted = false } = {}) => {
    try {
      const params = { page, page_size: pageSize };
      if (status) params.status = status;
      if (q) params.q = q;
      if (campaignId) params.campaign_id = campaignId;
      if (ownerId) params.owner_id = ownerId;
      if (deletedOnly) params.deleted_only = true;
      if (includeDeleted) params.include_deleted = true;
      const r = await apiService.getSecure(BASE, { params });
      return r.data;
    } catch (e) {
      console.error("SequenceService.list:", e);
      throw e;
    }
  },

  get: async (id) => {
    const r = await apiService.getSecure(`${BASE}/${id}`);
    return r.data;
  },

  create: async (payload) => {
    const r = await apiService.postSecure(BASE, payload);
    return r.data;
  },

  update: async (id, payload) => {
    const r = await apiService.patch(`${BASE}/${id}`, payload);
    return r.data;
  },

  // Read-only preview of what deleting would stop (candidates, pending
  // messages). Powers the delete confirmation dialog.
  deleteImpact: async (id) => (await apiService.getSecure(`${BASE}/${id}/delete-impact`)).data,

  // Things to flag before starting — steps whose template was deleted, and
  // which steps are still unconfirmed (OUTREACH_OVERHAUL.md §7).
  warnings: async (id) => (await apiService.getSecure(`${BASE}/${id}/warnings`)).data,

  // Deleting also SHUTS THE SEQUENCE DOWN: enrolled candidates are stopped and
  // scheduled messages cancelled. Returns the counts so the UI can confirm it.
  remove: async (id) => {
    const r = await apiService.delete(`${BASE}/${id}`);
    return r.data;
  },

  // `targets`: [{ candidateId, applicationId }] — the application the Start
  // dialog showed per candidate (SequenceService.candidates() below returns
  // one row per application, so the dialog always has one to send).
  start: async (id, targets, senderId = null) => {
    const r = await apiService.postSecure(`${BASE}/${id}/start`, {
      targets: targets.map((t) => ({ candidate_id: t.candidateId, application_id: t.applicationId })),
      sender_id: senderId,
    });
    return r.data;
  },

  pause: async (id) => (await apiService.postSecure(`${BASE}/${id}/pause`)).data,
  resume: async (id) => (await apiService.postSecure(`${BASE}/${id}/resume`)).data,
  archive: async (id) => (await apiService.postSecure(`${BASE}/${id}/archive`)).data,
  restore: async (id) => (await apiService.postSecure(`${BASE}/${id}/restore`)).data,

  previewSchedule: async (id, candidateIds) => {
    const r = await apiService.postSecure(`${BASE}/${id}/preview-schedule`, {
      candidate_ids: candidateIds,
    });
    return r.data;
  },

  // Steps
  addStep: async (id, payload) => (await apiService.postSecure(`${BASE}/${id}/steps`, payload)).data,
  updateStep: async (id, stepId, payload) => (await apiService.patch(`${BASE}/${id}/steps/${stepId}`, payload)).data,
  deleteStep: async (id, stepId) => (await apiService.delete(`${BASE}/${id}/steps/${stepId}`)).data,

  // Step approval (OUTREACH_OVERHAUL.md §7): Confirm writes approved_subject/
  // approved_body/approved_at/approved_by from the step's current draft.
  confirmStep: async (id, stepId) => (await apiService.postSecure(`${BASE}/${id}/steps/${stepId}/confirm`)).data,
  // Drafts { subject, body } from the step's saved brief and the sequence's
  // tone guide. Persists nothing — the caller places the draft in the fields.
  generateStepDraft: async (id, stepId) => (await apiService.postSecure(`${BASE}/${id}/steps/${stepId}/generate`)).data,

  // The one served variable list — the picker and Confirm's client precheck
  // both read this instead of a locally duplicated array.
  variables: async () => (await apiService.getSecure(`/api/v2/outreach/variables`)).data,

  // Enrollments (candidate visibility + progress)
  enrollments: async (id, { status, q } = {}) => {
    const params = {};
    if (status) params.status = status;
    if (q) params.q = q;
    const r = await apiService.getSecure(`${BASE}/${id}/enrollments`, { params });
    return r.data;
  },
  unenroll: async (id, candidateId) => (await apiService.delete(`${BASE}/${id}/enrollments/${candidateId}`)).data,
  bulkUnenroll: async (id, candidateIds) => (await apiService.postSecure(`${BASE}/${id}/enrollments/bulk-unenroll`, { candidate_ids: candidateIds })).data,

  // Candidates eligible for outreach — one row per non-deleted application on
  // a visible job, no status filter (Start dialog source). `excludeSequenceId`
  // leaves out anyone already enrolled on that sequence.
  candidates: async ({ jobId, q, excludeSequenceId, page = 1, pageSize = 100 } = {}) => {
    const params = { page, page_size: pageSize };
    if (jobId) params.job_id = jobId;
    if (q) params.q = q;
    if (excludeSequenceId) params.exclude_sequence_id = excludeSequenceId;
    const r = await apiService.getSecure(`/api/v2/outreach/candidates`, { params });
    return r.data;
  },

  // Duplicate a sequence (config + steps) into a new draft
  clone: async (id, name = null) => (await apiService.postSecure(`${BASE}/${id}/clone`, { name })).data,

  // Messages for a sequence (used to surface failed/skipped delivery issues)
  messages: async (id, { status, limit = 200 } = {}) => {
    const params = { sequence_id: id, limit };
    if (status) params.status = status;
    const r = await apiService.getSecure(`/api/v2/outreach/messages`, { params });
    return r.data;
  },
};

export default SequenceService;
